import React, { useEffect, useState, type FormEvent } from "react";
import type { NodeProps, Node } from "@xyflow/react";
import type { ExtraNodeData, InterfaceToRecord } from "./action-config-node";
import ActionConfigNode from "./action-config-node";
import type { SetVehicleRelayData } from "../../../api";
import { VehicleRelay } from "../../../api";
import type { IChoiceGroupOption } from "@fluentui/react";
import { ChoiceGroup, mergeStyles, TooltipHost } from "@fluentui/react";
import { useTranslation } from "react-i18next";
import { TriangleAlert } from "lucide-react";
import colors from "../../../ui/colors.scss";
import { useId } from "@fluentui/react-hooks";
import { useUpdateNodeData } from "@/lib/hooks/reactflow";

type SetVehicleRelayNode = Node<InterfaceToRecord<SetVehicleRelayData & ExtraNodeData>>;

export default function SetVehicleRelay(props: NodeProps<SetVehicleRelayNode>): JSX.Element {
    const { t } = useTranslation();
    const [selectedRelay, setSelectedRelay] = useState(props.data.relay ?? VehicleRelay.ONE);
    const optionRootClass = mergeStyles({
        display: "flex",
        alignItems: "center",
        gap: "10px",
        position: "relative",
    });
    const tooltipId = useId("tooltip");
    const relayOptions: IChoiceGroupOption[] = Object.values(VehicleRelay).map((key, index) => {
        const option: IChoiceGroupOption = {
            key,
            text: t(`component.actionConfigNodes.relay.${index}`),
        };
        // Show warnings for relay 1 and 3:
        // Relay 1: Reserved for ELOfleet engine control when using ELOfusion; only available with ELOshield SPRINT.
        // Relay 3: Not supported on ELObox 1.5 (PCB has only two relays).
        if (key === VehicleRelay.ONE || key === VehicleRelay.THREE) {
            option.onRenderField = (props, render) => {
                return (
                    <TooltipHost
                        id={`${tooltipId}-relay-${key}`}
                        content={t(`component.actionConfigNodes.relay.${index}.tooltip`)}
                    >
                        <div className={optionRootClass}>
                            {render!(props)}
                            <TriangleAlert size="18" color={colors["secondarydashboard-medium"]} />
                        </div>
                    </TooltipHost>
                );
            };
        }
        return option;
    });
    const updateNode = useUpdateNodeData<SetVehicleRelayNode>(props.id);

    // We have to set the data to prevent undefined values
    useEffect(() => {
        updateNode({ relay: selectedRelay });
    }, [selectedRelay, updateNode]);

    function onChange(
        _e?: FormEvent<HTMLElement | HTMLInputElement>,
        option?: IChoiceGroupOption,
    ): void {
        if (!option) {
            return;
        }
        setSelectedRelay(option.key as VehicleRelay);
    }

    return (
        <ActionConfigNode nodeKind={props.type} {...props.data}>
            <ChoiceGroup options={relayOptions} selectedKey={selectedRelay} onChange={onChange} />
        </ActionConfigNode>
    );
}
